import React from 'react';
import { Button } from '@/components/ui/Button';
import { BaseLink } from '@/components/ui/BaseLink';
import { LegalFooter } from '@/components/ui/Footer/LegalFooter';

interface LegalPageWrapperProps {
  children: React.ReactNode;
  title: string;
  lastUpdatedAt: string;
  links?: { title: string; href: string; active?: boolean }[];
}

export const LegalPageWrapper: React.FC<LegalPageWrapperProps> = ({
  children,
  title,
  lastUpdatedAt,
  links,
}) => (
  <article className="legal-page-wrapper">
    <header className="legal-page-wrapper__header">
      <div className="container">
        {links && Boolean(links.length) && (
          <ul className="legal-page-wrapper__links-list">
            {links.map((link) => (
              <li
                key={`${link.title}-${link.href}`}
                className="legal-page-wrapper__links-list-item"
              >
                <BaseLink href={link.href}>
                  <Button
                    type="button"
                    variant={link.active ? 'primary' : 'secondary'}
                    tabIndex={-1}
                  >
                    {link.title}
                  </Button>
                </BaseLink>
              </li>
            ))}
          </ul>
        )}
        <div className="legal-page-wrapper__header-content">
          <h1 className="legal-page-wrapper__header-title">{title}</h1>
          <span className="legal-page-wrapper__header-subtitle">Last Updated: {lastUpdatedAt}</span>
        </div>
      </div>
    </header>
    <section className="legal-page-wrapper__content">
      <div className="container">{children}</div>
    </section>
    <LegalFooter />

    {/* Styles */}
    <style jsx>
      {`
        .legal-page-wrapper {
          @apply flex flex-col;
          @apply w-full min-h-full;
          @apply font-dm-sans leading-normal text-primary-800;
          @apply pt-header-mobile lg:pt-header-desktop;

          &__links-list {
            @apply pb-6 md:pb-10 flex flex-wrap -mx-2;

            &-item {
              @apply m-2;
            }
          }

          &__header {
            @apply pt-4 md:pt-6;

            &-content {
              @apply flex flex-col md:flex-row md:items-center justify-between;
            }

            &-title {
              @apply flex-grow;
              @apply text-4xl font-bold;
            }
            &-subtitle {
              @apply mt-3 md:mt-0 text-base;
            }
          }

          &__content {
            @apply py-6;
          }
          :global(&__content) {
            @apply font-dm-sans leading-normal text-primary-800;
          }
          :global(&__content h2, h3, h4, h5, h6) {
            @apply mt-[1em] mb-[0.5em] font-bold;
          }
          :global(&__content h2) {
            @apply text-2xl;
          }
          :global(&__content h3) {
            @apply text-xl;
          }
          :global(&__content h4) {
            @apply text-lg;
          }
          :global(&__content h5) {
            @apply text-base;
          }
          :global(&__content h6) {
            @apply text-sm;
          }
          :global(&__content p) {
            @apply text-base leading-normal my-[1em];
          }
          :global(&__content a) {
            @apply text-indigo-700 underline;
          }
          :global(&__content ul, &__content ol) {
            @apply list-inside pl-3;
          }
          :global(&__content ul > li:nth-child(1n + 2), &__content ol > li:nth-child(1n + 2)) {
            @apply mt-3;
          }
          :global(&__content ul) {
            @apply list-disc;
          }
          :global(&__content ol) {
            @apply list-decimal;
          }
          :global(&__content table) {
            @apply border border-solid border-primary-300;
            @apply table-auto border-collapse;
            @apply w-full;
          }
          :global(&__content table > caption) {
            @apply mb-3 font-bold;
          }
          :global(&__content table th) {
            @apply bg-primary-100;
          }
          :global(&__content table th, &__content table td) {
            @apply p-4;
            @apply border border-solid border-primary-300;
          }
        }
      `}
    </style>
  </article>
);
